/**
 * 🌸 PINK THEME
 *
 * Розовая тема с розовым акцентом.
 * Полностью самодостаточна, содержит все цвета напрямую.
 *
 * Архитектура: все цвета определены непосредственно в теме,
 * без ссылок на внешние примитивы или другие файлы.
 */

import type { ThemeTokens } from '../semantic';
import { createThemeWithDerivations } from '../color-derivations';

const basePinkTheme = {
  // ============================================
  // 📐 SURFACES (поверхности)
  // ============================================
  bg: '#F0E7EC',                          // Дальний слой, темнее (светло-розовый)
  surface: '#F7EFF4',                     // Средний слой, светлее (розоватый)
  card: '#FDF8FB',                        // Розоватый для карточек
  elevated: '#FFFCFE',                    // Теплый белый для приподнятых (в палитре)

  // ============================================
  // 📝 TEXT (цвета текста для розовой темы)
  // ============================================
  text: '#1F2328',                        // Темно-серый основной текст
  textMuted: '#5B616A',                   // Средне-серый вторичный текст
  textTertiary: '#667179',                // Третичный текст (контраст ~4.50 на bg, ~4.79 на surface)
  textPlaceholder: '#868A92',             // Плейсхолдеры (контраст ~3.12 на bg, читаемый)

  // ============================================
  // 🟡 PRIMARY (основной акцент)
  // ============================================
  primary: '#F48FB1',                     // Пастельный розовый акцент
  onPrimary: '#1F2328',                   // Темный текст на пастельном розовом
  link: '#B01C5E',                        // Спокойный розовый для ссылок/иконок
  onLightSurface: '#1B1B1B',              // Темный текст на светлых плашках (статусы, карточки)

  // ============================================
  // 📊 BORDERS (границы)
  // ============================================
  border: '#E6D8E0',                      // Мягкие розоватые границы
  borderStrong: '#D2C1CC',                // Средние розовые границы (не совпадает с disabled)

  // ============================================
  // ✅ SEMANTIC COLORS (семантические цвета)
  // ============================================
  success: '#4E8A67',
  onSuccess: '#0B0B0B',                   // Контрастный черный для WCAG AA
  warning: '#B9823A',
  onWarning: '#0B0B0B',                   // Контрастный черный для WCAG AA
  error: '#C85C5C',
  onError: '#0B0B0B',                     // Контрастный черный для WCAG AA
  info: '#4E7FA8',
  onInfo: '#0B0B0B',                      // Контрастный черный для WCAG AA

  // ============================================
  // 🎯 INTERACTIVE (интерактивные состояния)
  // ============================================
  focus: 'rgba(244, 143, 177, 0.28)',      // Кольцо фокуса от primary (#F48FB1)
  disabled: '#898D91',                     // Отключенный текст (контраст ~3.01 на bg, читается)

  // ============================================
  // 🌑 SCRIM (затемнение)
  // ============================================
  scrim: 'rgba(0, 0, 0, 0.05)',             // Заблюренный чёрный 5%

  // ============================================
  // 📜 SCROLLBAR (полоса прокрутки графика аналитики)
  // ============================================
  scrollbarTrack: '#F7EFF4',   // surface
  scrollbarThumb: '#E6D8E0',   // border
  scrollbarThumbHover: '#5B616A', // textMuted

  // ============================================
  // 📅 APPOINTMENT STATUSES (статусы записей)
  // ============================================
  apptScheduledBg: '#BBDEFB', // Blue 100
  apptCompletedBg: '#DCEDC8', // Light Green 100 (теплее)
  apptNoShowBg: '#FFE0B2', // Orange 100 (более оранжевый)
  apptCancelledBg: '#FFCDD2', // Red 100
  apptMovedBg: '#E1BEE7', // Purple 100
  apptBreakBg: '#CFD8DC', // Blue Grey 100
};

export const pinkTheme: ThemeTokens = createThemeWithDerivations(basePinkTheme, 'pink');